// src/content.js
//
// The site's own copy of everything editable, in exactly the shape the portal
// stores. This is the fallback, not the source of truth: ContentContext
// fetches the edited document and merges it over this on mount.
//
// Keeping a full copy here rather than fetching and waiting means the page
// renders instantly, a crawler sees real text without a second request, and
// an unreachable API leaves the site intact.
//
// If you change a key here, change it in the portal's contentSchema.js too or
// the field becomes uneditable.

export const DEFAULTS = {
  hero: {
    heading: "New refrigeration, installed and commissioned",
    body: "Supply and installation of new commercial refrigeration systems, from cold rooms and freezer rooms to full shop setups for butcheries, restaurants and retail outlets.",
    stats: [
      { value: "2000", label: "Established" },
      { value: "850+", label: "Clients" },
      { value: "Gauteng", label: "Coverage" },
    ],
  },

  // The data plate beside the headline.
  plate: [
    { label: "Type", value: "Cold / freezer room" },
    { label: "Application", value: "Commercial" },
    { label: "Scope", value: "Supply, install, commission" },
    { label: "Support", value: "Warranty + maintenance" },
  ],

  installations: {
    eyebrow: "What we install",
    heading: "From a single cold room to a complete fit-out",
    body1:
      "Brooke Cold Storage specialises in the supply and installation of new commercial refrigeration systems. That runs from a single walk-in cold room through to the full refrigeration fit-out of a new shop.",
    body2:
      "The team handles everything from equipment supply and pipework to unit setup and commissioning, so the installation is one contract from start to finish rather than three trades to coordinate.",
    items: [
      {
        title: "Cold rooms and freezer rooms",
        body: "Panel supply, build and the refrigeration plant that serves them, sized to the load rather than the catalogue.",
      },
      {
        title: "Complete fit-outs for new businesses",
        body: "Full refrigeration setups for butcheries, restaurants and retail outlets opening a new site.",
      },
      {
        title: "Custom refrigeration solutions",
        body: "Where the standard product does not fit the space or the product being stored.",
      },
    ],
  },

  process: {
    eyebrow: "How an installation runs",
    heading: "Four stages, one contract",
    steps: [
      {
        title: "Specification",
        body: "Load, layout and product determine panel spec and plant size. Getting this stage right is what sets the running cost for the life of the room.",
      },
      {
        title: "Supply",
        body: "Equipment and parts sourced across a range of manufacturers, so the quote reflects the best available cost rather than one supplier's list.",
      },
      {
        title: "Installation",
        body: "Panels, pipework and plant installed by qualified technicians. One team on site rather than separate trades to coordinate.",
      },
      {
        title: "Commissioning",
        body: "The system is set up, run and handed over working, with the after-installation support and warranty that follows it.",
      },
    ],
  },

  why: {
    eyebrow: "Why Brooke",
    heading: "Installed by the people who service it",
    body: "Whether you are opening a new butchery, restaurant or retail outlet, Brooke Cold Storage delivers complete refrigeration solutions that combine quality, efficiency and durability.",
    points: [
      {
        title: "Qualified technicians",
        body: "Expert installation and system commissioning, not a subcontracted crew.",
      },
      {
        title: "Turnkey setups",
        body: "Complete refrigeration for a new commercial space, handled as one job.",
      },
      {
        title: "Support afterwards",
        body: "Reliable after-installation support, warranty and ongoing maintenance.",
      },
    ],
  },

  sectors: {
    eyebrow: "Who we install for",
    heading: "Sectors",
    list: [
      "Butcheries",
      "Restaurants",
      "Retail outlets",
      "Supermarkets",
      "Food processing",
      "Cold chain",
    ],
  },

  areas: [
    "Pretoria",
    "Akasia",
    "Centurion",
    "Midrand",
    "Johannesburg",
    "Ekurhuleni",
  ],

  contact: {
    phoneLabel: "078 220 5037",
    phone: "+27782205037",
    street: "34 Joan Road, Winternest AH",
    locality: "Akasia",
    region: "Gauteng",
    postal: "0200",
  },

  // Feeds subject_ref on the enquiry, so the office knows what it is about
  // without reading the message. Changing a ref breaks the link to enquiries
  // already stored against it.
  enquirySubjects: [
    { ref: "cold-room", label: "Cold room or freezer room" },
    { ref: "shop-fitout", label: "Complete shop fit-out" },
    { ref: "custom", label: "Custom refrigeration solution" },
    { ref: "supermarket", label: "Supermarket refrigeration" },
    { ref: "other", label: "Something else" },
  ],
};

// Not editable. Structural facts about where this site sits in the estate,
// which change only when the estate itself does.
export const SITE = {
  division: "Division 02",
  name: "Brooke Cold Storage",
  gateway: "https://brookerefrigeration.co.za",
  siblings: [
    { label: "Maintenance", href: "https://maintenance.brookerefrigeration.com" },
    { label: "Requip", href: "https://requip.brookerefrigeration.com" },
  ],
};